import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CameraIcon } from "lucide-react";

/**
 * BusinessCheckInPhotos
 *
 * Renders the "Check-ins from customers" section on a public business profile.
 * Pulls from `GET /businesses/{bid}/checkin-photos` — the public feed that
 * excludes Strict-Mode-flagged shots. Names are pre-masked server-side.
 *
 * Hidden entirely when the owner has opted out (`hidden_by_owner: true`) OR
 * there are zero clean check-in photos — keeps the profile from showing an
 * awkward empty card.
 */
function relative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(1, Math.round((now - then) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BusinessCheckInPhotos({ businessId }) {
  const [items, setItems] = useState([]);
  const [hiddenByOwner, setHiddenByOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/businesses/${businessId}/checkin-photos`)
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data.items || []);
        setHiddenByOwner(!!data.hidden_by_owner);
      })
      .catch(() => { /* endpoint 404s → hide silently */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  if (loading) return null;
  if (hiddenByOwner) return null;
  if (items.length === 0) return null;

  return (
    <Card
      className="rounded-2xl border border-border mt-8 p-6 shadow-sm bg-white"
      data-testid="business-checkins-section"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <div className="h-8 w-1 rounded bg-[#B31E1E]" aria-hidden="true" />
        <CameraIcon size={16} className="text-[#B31E1E]" />
        <h3 className="font-display text-2xl">Check-ins from customers</h3>
        <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-[#B31E1E]/30 text-[#B31E1E]" data-testid="business-checkins-count">
          {items.length} recent
        </Badge>
      </div>
      <p className="text-sm text-gray-500 mt-1 ml-3 max-w-2xl">
        Verified loyalty check-ins from people who actually visited — GPS confirmed at the door.
      </p>

      <div
        className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
        data-testid="business-checkins-grid"
      >
        {items.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setLightbox(p)}
            className="group relative aspect-square rounded-md overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#B31E1E]/60"
            data-testid={`business-checkin-${p.id}`}
          >
            <img
              src={p.photo_data_url}
              alt={`Check-in by ${p.customer_name}`}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-left">
              <div className="text-white text-xs font-mono truncate" title={p.customer_name}>{p.customer_name}</div>
              <div className="text-white/70 text-[10px] font-mono">{relative(p.created_at)}</div>
            </div>
          </button>
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="business-checkins-lightbox"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.photo_data_url}
              alt={`Check-in by ${lightbox.customer_name}`}
              className="w-full max-h-[80vh] object-contain rounded-md"
            />
            <div className="mt-3 flex items-center justify-between text-white text-sm">
              <div>
                <div className="font-mono">{lightbox.customer_name}</div>
                <div className="text-white/60 text-xs">{relative(lightbox.created_at)}</div>
              </div>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="rounded-[35px] px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-mono"
                data-testid="business-checkins-lightbox-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
